import { FC, Fragment } from 'react';

import { CampaignProductType } from 'utils/constants/campaign';

import { ActionBar, BottomSheet, Button, Divider, Flex, Link, Text } from '@hh.ru/magritte-ui';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import {
    CAMPAIGN_CARD_DETAILS_COLUMNS,
    CAMPAIGNS_TABLE_COLUMNS_MAP,
    CampaignsTableColumn,
} from 'components/CampaignsDashboard/components/CampaignsTable/columns';
import { CampaignTableCell } from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignTableCell';
import { MobileTableHeaderTooltip } from 'components/CampaignsDashboard/components/CampaignsTable/components/MobileTableHeaderTooltip';
import { CELL_TYPOGRAPHY } from 'components/CampaignsDashboard/utils/constants';

import { CampaignDetails, CampaignStatistics, DashboardCampaign } from 'store/models/campaignsDashboard';

import styles from 'components/CampaignsDashboard/components/CampaignsTable/components/CampaignCard/CampaignCard.module.less';

const TrlKeys = {
    close: 'common.close',
    showMore: 'CampaignsDashboard.CampaignsTable.CampaignCard.showMore',
    productType: 'CampaignsDashboard.CampaignsTable.mobile.column.productType',
};

const getMobileColumnTrlKey = (column: CampaignsTableColumn): string => {
    if (column === CampaignsTableColumn.ProductType) {
        return TrlKeys.productType;
    }
    return CAMPAIGNS_TABLE_COLUMNS_MAP[column].trlKey;
};

interface CampaignCardDetailsBottomSheetProps {
    campaign: DashboardCampaign;
    campaignStatistics?: CampaignStatistics;
    campaignDetails?: CampaignDetails;
    isStatisticsLoading: boolean;
    isDetailsLoading: boolean;
    isStatusToggleLoading: boolean;
    onToggleStatus: () => void;
    isInsufficientBalance: boolean;
}

export const CampaignCardDetailsBottomSheet: FC<CampaignCardDetailsBottomSheetProps> = ({
    campaign,
    campaignStatistics,
    campaignDetails,
    isStatisticsLoading,
    isDetailsLoading,
    isStatusToggleLoading,
    onToggleStatus,
    isInsufficientBalance,
}) => {
    const trls = useTrls();
    const [visible, setVisibleOn, setVisibleOff] = useOnOffState(false);

    const columns = CAMPAIGN_CARD_DETAILS_COLUMNS.filter(
        (column) => column !== CampaignsTableColumn.Banners || campaign.productType === CampaignProductType.Cpc
    );

    return (
        <>
            <Link Element="button" onClick={setVisibleOn} data-qa={`campaign-show-more-${campaign.id}`}>
                <Text typography="label-3-regular">{trls(TrlKeys.showMore)}</Text>
            </Link>
            <BottomSheet
                visible={visible}
                onClose={setVisibleOff}
                height="content"
                data-qa={`campaign-details-bottom-sheet-${campaign.id}`}
                footer={
                    <ActionBar
                        primaryActions={
                            <Button
                                stretched
                                size="medium"
                                mode="secondary"
                                style="neutral"
                                onClick={setVisibleOff}
                                data-qa={`campaign-details-close-${campaign.id}`}
                            >
                                {trls(TrlKeys.close)}
                            </Button>
                        }
                    />
                }
            >
                <Flex direction="column" minWidth={0}>
                    {columns.map((column, index) => {
                        const tooltipTrlKey = CAMPAIGNS_TABLE_COLUMNS_MAP[column].tooltipTrlKey;
                        const columnTitle = trls(getMobileColumnTrlKey(column));

                        return (
                            <Fragment key={column}>
                                <Flex align="center" justify="space-between" gap={12} minWidth={0} py={16}>
                                    <Flex align="center" gap={4}>
                                        <Text typography={CELL_TYPOGRAPHY}>{columnTitle}</Text>
                                        {tooltipTrlKey && (
                                            <MobileTableHeaderTooltip
                                                title={columnTitle}
                                                tooltipText={trls(tooltipTrlKey)}
                                                dataQa={`campaign-mobile-column-${column}-tooltip`}
                                            />
                                        )}
                                    </Flex>
                                    <Flex justify="flex-start" className={styles.detailsValue}>
                                        <CampaignTableCell
                                            columnKey={column}
                                            campaign={campaign}
                                            campaignStatistics={campaignStatistics}
                                            campaignDetails={campaignDetails}
                                            isStatisticsLoading={isStatisticsLoading}
                                            isDetailsLoading={isDetailsLoading}
                                            isStatusToggleLoading={isStatusToggleLoading}
                                            onToggleStatus={onToggleStatus}
                                            isInsufficientBalance={isInsufficientBalance}
                                            isMobile
                                            typography={CELL_TYPOGRAPHY}
                                        />
                                    </Flex>
                                </Flex>
                                {index < columns.length - 1 && <Divider />}
                            </Fragment>
                        );
                    })}
                </Flex>
            </BottomSheet>
        </>
    );
};
